import React, { useEffect } from "react";
import { SiteHeader, SiteFooter } from "./SiteChrome";
import { CONTACT } from "./siteConfig";
import "./Site.css";

const LAST_UPDATED = "29 October 2026";

export default function SitePrivacyPage() {
  useEffect(() => {
    document.title = "Privacy Policy — DecisionFlow";
  }, []);

  /* The app shell locks html/body to a non-scrolling 100% viewport and paints
     its own background. Released here for as long as this page is mounted. */
  useEffect(() => {
    document.documentElement.classList.add("site-html");
    document.body.classList.add("site-body");
    return () => {
      document.documentElement.classList.remove("site-html");
      document.body.classList.remove("site-body");
    };
  }, []);

  return (
    <div className="site">
      <SiteHeader />

      <main id="top">
        <section className="site-section">
          <div className="site-container site-legal">
            <p className="site-eyebrow">Legal</p>
            <h1>Privacy Policy</h1>
            <p className="site-legal-updated">Last updated {LAST_UPDATED}</p>

            <p>
              This policy covers DecisionFlow — the Project &amp; Document
              Management and Manufacturing &amp; Production Tracking
              applications operated by Hexasphere ("we", "us"). It explains
              what we collect from a tenant company and its users, why, and
              what a customer's rights are over that data.
            </p>

            <h2>What we collect</h2>
            <ul>
              <li>
                <strong>Account and profile data</strong> — name, email,
                phone number, role and the shift/attendance details your
                company enters for its staff.
              </li>
              <li>
                <strong>Work data</strong> — projects, workflow steps,
                manufacturing orders, parts, stock movements, files and
                comments your company creates while using the product.
              </li>
              <li>
                <strong>Messaging data</strong> — when your company enables
                WhatsApp or Telegram updates, we process the phone number or
                chat ID needed to deliver those messages and the replies
                operators send back (e.g. quantities reported at a step).
              </li>
              <li>
                <strong>Usage data</strong> — login activity and basic
                technical logs (IP address, browser) kept for security and
                troubleshooting.
              </li>
            </ul>

            <h2>How we use it</h2>
            <p>
              Solely to run the product for your company: authenticating
              users, tracking work through its steps, sending the alerts and
              daily summaries you've configured, generating the reports you
              download, and billing. We do not sell customer data, and we do
              not use one tenant's data to train or improve a model shared
              with other tenants.
            </p>

            <h2>Who we share it with</h2>
            <p>
              Data is shared only where the product's own features require
              it: with WhatsApp/Telegram, to deliver the messages your
              company sends through the app, and with our hosting provider,
              to store it. If your company runs a self-hosted deployment
              ("on yours" — see the pricing section of the site), work data
              stays on infrastructure you control and does not pass through
              our servers at all. If someone in your company connects an
              AI assistant such as Claude, the data it reads is shared with
              that assistant's provider — see{" "}
              <a href="#claude-connector">AI assistant connector</a> below.
            </p>

            <h2>Customer portal links</h2>
            <p>
              Share links generated for your clients (the <code>/portal/:token</code>{" "}
              pages) expose only the specific project or document your team
              chose to share, to whoever holds the link. Clients do not get a
              login of their own and cannot see anything else in your
              account.
            </p>

            <h2 id="claude-connector">AI assistant connector (Claude, ChatGPT, Codex)</h2>
            <p>
              DecisionFlow offers an optional, read-only connector that lets
              an AI assistant such as Anthropic's Claude or OpenAI's ChatGPT
              and Codex read your workspace on your behalf (see{" "}
              <a href="/ai-assistants">how it works</a>). It is
              off until a user in your company connects it, and nothing is
              sent to an assistant before then.
            </p>
            <ul>
              <li>
                <strong>What the assistant receives.</strong> Only the
                results of what it asks for while answering you — for
                example a project's stages and assignees, a task and its
                comments, or audit entries — and only what the connecting
                user is allowed to see in DecisionFlow. People appear by
                name; the email addresses and phone numbers on their user
                profiles are left out, though anything your team typed into
                a comment, description or custom field, and the contents of
                a file attached to a task when the assistant opens it, are
                passed on as they are. The assistant cannot change anything in your
                account.
              </li>
              <li>
                <strong>Your own email address.</strong> Some assistants —
                ChatGPT and Codex among them — ask to identify the account
                you connect with. When they do, the consent screen says so,
                and we share your name or email address with that provider
                only if it asked for them.
              </li>
              <li>
                <strong>What happens to it there.</strong> Once delivered,
                that data is handled by the assistant's provider under its
                own terms and privacy policy, and under the agreement your
                company has with that provider. See{" "}
                <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer">
                  Anthropic's privacy policy
                </a>{" "}
                for Claude and{" "}
                <a href="https://openai.com/policies/privacy-policy/" target="_blank" rel="noopener noreferrer">
                  OpenAI's privacy policy
                </a>{" "}
                for ChatGPT and Codex.
              </li>
              <li>
                <strong>What we keep.</strong> The name and callback address
                of each connected assistant app; a record of each connection
                (who approved it, when it was created, last used, and when it
                expires or was revoked); and entries in your company's audit
                trail when a connection is made or removed. Access tokens are
                stored only as one-way hashes. We do not keep a copy of what
                the assistant read beyond our normal error logs.
              </li>
              <li>
                <strong>How long.</strong> A connection lasts up to 90 days
                unless the user, or an admin in your company, revokes it
                sooner under AI Access, which takes effect immediately. Records of past connections
                are kept with your account's other audit records.
              </li>
            </ul>

            <h2>Data retention</h2>
            <p>
              We keep your company's data for as long as your account is
              active. If you close your account, we delete work data within
              a reasonable period afterward, except where we're required to
              keep records for legal or accounting reasons.
            </p>

            <h2>Security</h2>
            <p>
              Access to work data is scoped by the roles and permissions your
              company configures. Passwords are stored hashed, not in plain
              text. We restrict internal access to customer data to what's
              needed to operate and support the product.
            </p>

            <h2>Your rights</h2>
            <p>
              You can ask us to export or delete the data associated with
              your account, or a specific user within it, at any time —
              contact us using the details below. Individual users should
              first raise this with their company's administrator, since
              account and work data belongs to the tenant company, not the
              individual user.
            </p>

            <h2>Changes to this policy</h2>
            <p>
              If this policy changes in a way that affects how your data is
              handled, we'll update the date at the top of this page and
              notify account administrators.
            </p>

            <h2>Contact</h2>
            <p>
              Questions about this policy or a data request:{" "}
              <a href={`mailto:${CONTACT.supportEmail}`}>{CONTACT.supportEmail}</a> or{" "}
              <a href={`tel:${CONTACT.phoneHref}`}>{CONTACT.phone}</a>.
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
